import {
  AdminPricingApiService,
  CABINET_PATHS,
  computeStorageMarketingMaxDiscountPercent,
  diffDomainPricingConfigs,
  todayMinskIsoDate,
  type AdminDomainPricingVersionDetailResponse,
  type PricingDomain,
  type PricingDomainConfigMap,
} from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, map, startWith } from 'rxjs';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import {
  NotInput,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import {
  extractDomainConfig,
  FURNITURE_COMPLEXITIES,
  MOVING_RATE_FIELDS,
  coercePricingNumber,
  mergeStorageServiceRatesForForm,
  normalizeDomainConfigForApi,
  pricingMax,
  pricingMin,
} from './staff-pricing.util';

@Component({
  selector: 'lib-staff-pricing-domain-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslateModule, NotInput, CabinetButtonComponent],
  templateUrl: './staff-pricing-domain-form.component.html',
  styleUrl: './staff-pricing-domain-form.component.scss',
})
export class StaffPricingDomainFormComponent implements OnInit {
  readonly domain = input.required<PricingDomain>();
  readonly versionId = input<string | null>(null);

  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #pricingApi = inject(AdminPricingApiService);
  readonly #fb = inject(FormBuilder);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #translate = inject(TranslateService);

  protected readonly loading = signal(true);
  protected readonly submitting = signal(false);
  protected readonly baselineConfig = signal<unknown | null>(null);
  protected readonly editingVersion = signal<
    AdminDomainPricingVersionDetailResponse<PricingDomain> | null
  >(null);
  protected readonly minEffectiveDate = todayMinskIsoDate();

  protected readonly isEditMode = computed(
    () => this.editingVersion() !== null,
  );

  protected readonly movingRateFields = MOVING_RATE_FIELDS;
  protected readonly furnitureComplexities = FURNITURE_COMPLEXITIES;

  protected readonly form = this.#fb.group({
    effectiveFrom: [
      todayMinskIsoDate(),
      [Validators.required, Validators.pattern(/^\d{4}-\d{2}-\d{2}$/)],
    ],
    comment: [''],
    storage: this.#fb.group({
      baseRateByndPerM3Day: [0, [Validators.required, pricingMin(0.01)]],
      minVolumeM3: [0, [Validators.required, pricingMin(0.01)]],
      maxAutoStandardDiscountPercent: [{ value: 0, disabled: true }],
      maxPromoDiscountPercent: [
        0,
        [Validators.required, pricingMin(0), pricingMax(1)],
      ],
      serviceRates: this.#fb.group({
        pickupByndPerM3: [0, [Validators.required, pricingMin(0.01)]],
        returnByndPerM3: [0, [Validators.required, pricingMin(0.01)]],
        packagingByndPerM3: [0, [Validators.required, pricingMin(0.01)]],
        pickupMinVolumeM3: [0, [Validators.required, pricingMin(0.01)]],
        returnMinVolumeM3: [0, [Validators.required, pricingMin(0.01)]],
        packagingMinVolumeM3: [0, [Validators.required, pricingMin(0.01)]],
      }),
    }),
    volumeDiscountTiers: this.#fb.array<FormGroup>([]),
    movingRates: this.#fb.group(
      Object.fromEntries(
        MOVING_RATE_FIELDS.map((field) => [
          field,
          [0, [Validators.required, pricingMin(0)]],
        ]),
      ),
    ),
    disassemblyRates: this.#fb.group(
      Object.fromEntries(
        FURNITURE_COMPLEXITIES.map((key) => [
          key,
          [0, [Validators.required, pricingMin(0)]],
        ]),
      ),
    ),
    assemblyRates: this.#fb.group(
      Object.fromEntries(
        FURNITURE_COMPLEXITIES.map((key) => [
          key,
          [0, [Validators.required, pricingMin(0)]],
        ]),
      ),
    ),
    promocodes: this.#fb.array<FormGroup>([]),
  });

  protected readonly formValue = toSignal(
    this.form.valueChanges.pipe(
      startWith(null),
      map(() => this.form.getRawValue()),
    ),
    { initialValue: this.form.getRawValue() },
  );

  protected readonly draftConfig = computed(() =>
    this.buildConfigFromForm(
      this.formValue() as ReturnType<typeof this.form.getRawValue>,
    ),
  );

  protected readonly diffEntries = computed(() => {
    const baseline = this.baselineConfig();
    const draft = this.draftConfig();
    if (!baseline || !draft) {
      return [];
    }
    return diffDomainPricingConfigs(this.domain(), baseline, draft);
  });

  protected readonly hasDiff = computed(() => this.diffEntries().length > 0);

  protected readonly hasMetadataChange = computed(() => {
    const editing = this.editingVersion();
    if (!editing) {
      return false;
    }
    const value = this.formValue();
    const effectiveFrom = value?.effectiveFrom ?? '';
    const comment = (value?.comment ?? '').trim();
    return (
      effectiveFrom !== editing.effective_from ||
      comment !== (editing.comment ?? '').trim()
    );
  });

  protected readonly canSubmit = computed(
    () => this.hasDiff() || this.hasMetadataChange(),
  );

  protected get volumeDiscountTiers(): FormArray<FormGroup> {
    return this.form.get('volumeDiscountTiers') as FormArray<FormGroup>;
  }

  protected get promocodes(): FormArray<FormGroup> {
    return this.form.get('promocodes') as FormArray<FormGroup>;
  }

  constructor() {
    effect(() => {
      if (this.domain() !== 'storage') {
        return;
      }

      const value = this.formValue();
      const tiers = (value?.volumeDiscountTiers ?? []).map((tier) => ({
        minVolumeM3: coercePricingNumber(tier?.['minVolumeM3']),
        discountPercent: coercePricingNumber(tier?.['discountPercent']),
      }));
      const maxPromo = coercePricingNumber(
        value?.storage?.maxPromoDiscountPercent,
      );
      const computed = computeStorageMarketingMaxDiscountPercent(
        tiers,
        maxPromo,
      );
      const control =
        this.form.controls.storage.controls.maxAutoStandardDiscountPercent;

      if (
        coercePricingNumber(control.getRawValue(), Number.NaN) !== computed
      ) {
        control.setValue(computed, { emitEvent: false });
      }
    });
  }

  public ngOnInit(): void {
    this.configureFormForDomain(this.domain());
    void this.loadBaseline();
  }

  /** Inactive domain sections stay disabled so hidden controls do not block submit. */
  private configureFormForDomain(domain: PricingDomain): void {
    const sectionControls: Record<PricingDomain, (FormGroup | FormArray)[]> = {
      storage: [this.form.controls.storage, this.volumeDiscountTiers],
      moving: [
        this.form.controls.movingRates,
        this.form.controls.disassemblyRates,
        this.form.controls.assemblyRates,
      ],
      promocodes: [this.promocodes],
    };

    for (const [key, controls] of Object.entries(sectionControls) as [
      PricingDomain,
      (FormGroup | FormArray)[],
    ][]) {
      const active = key === domain;
      for (const control of controls) {
        if (active) {
          control.enable({ emitEvent: false });
        } else {
          control.disable({ emitEvent: false });
        }
      }
    }
  }

  protected addVolumeTier(): void {
    this.volumeDiscountTiers.push(
      this.#fb.group({
        minVolumeM3: [0, [Validators.required, pricingMin(0)]],
        discountPercent: [
          0,
          [Validators.required, pricingMin(0), pricingMax(1)],
        ],
      }),
    );
  }

  protected removeVolumeTier(index: number): void {
    this.volumeDiscountTiers.removeAt(index);
  }

  protected addPromocode(): void {
    this.promocodes.push(
      this.#fb.group({
        promocode: ['', [Validators.required, Validators.minLength(2)]],
        discount: [
          10,
          [Validators.required, pricingMin(1), pricingMax(100)],
        ],
        commission: [null as number | null],
      }),
    );
  }

  protected removePromocode(index: number): void {
    this.promocodes.removeAt(index);
  }

  protected movingRateLabel(field: string): string {
    return this.#translate.instant(`admin.pricing.movingRates.${field}`);
  }

  protected furnitureComplexityLabel(key: string): string {
    return this.#translate.instant(`admin.pricing.furnitureComplexity.${key}`);
  }

  protected async onSubmit(): Promise<void> {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.canSubmit()) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.pricing.create.noDiff')],
      });
      return;
    }

    const draft = this.draftConfig();
    if (!draft) {
      return;
    }

    const effectiveFrom = this.form.controls.effectiveFrom.value;
    if (!effectiveFrom) {
      this.form.controls.effectiveFrom.markAsTouched();
      return;
    }

    const domain = this.domain();
    this.submitting.set(true);
    try {
      const comment = this.form.controls.comment.value?.trim();
      const payload = {
        effective_from: effectiveFrom,
        config: draft,
        comment: comment || undefined,
      };

      const editing = this.editingVersion();
      const saved = editing
        ? await firstValueFrom(
            this.#pricingApi.updateVersion(domain, editing.id, payload),
          )
        : await firstValueFrom(
            this.#pricingApi.createVersion(domain, payload),
          );

      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [
          this.#translate.instant(
            editing
              ? 'admin.pricing.updateSuccess'
              : 'admin.pricing.createSuccess',
          ),
        ],
      });
      void this.#router.navigate([
        this.#cabinetPaths.staff,
        'pricing',
        domain,
        saved.id,
      ]);
    } catch (error) {
      console.error('Failed to save pricing version:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant(
            this.isEditMode()
              ? 'admin.pricing.updateError'
              : 'admin.pricing.createError',
          ),
        ],
      });
    } finally {
      this.submitting.set(false);
    }
  }

  private async loadBaseline(): Promise<void> {
    this.loading.set(true);
    const domain = this.domain();
    try {
      const editId =
        this.versionId() ?? this.#route.snapshot.paramMap.get('id');
      if (editId) {
        await this.loadEditMode(domain, editId);
        return;
      }

      const response = await firstValueFrom(
        this.#pricingApi.getMergedForDate(todayMinskIsoDate()),
      );
      const config = structuredClone(
        extractDomainConfig(domain, response.config),
      );
      this.baselineConfig.set(config);
      this.patchFormFromConfig(domain, config);
    } catch (error) {
      console.error('Failed to load pricing baseline:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.pricing.loadError')],
      });
    } finally {
      this.loading.set(false);
    }
  }

  private async loadEditMode(
    domain: PricingDomain,
    id: string,
  ): Promise<void> {
    const [mergedResponse, editingResponse] = await Promise.all([
      firstValueFrom(this.#pricingApi.getMergedForDate(todayMinskIsoDate())),
      firstValueFrom(this.#pricingApi.getVersion(domain, id)),
    ]);

    if (!this.isFutureVersion(editingResponse)) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.pricing.editNotAllowed')],
      });
      void this.#router.navigate([
        this.#cabinetPaths.staff,
        'pricing',
        domain,
        editingResponse.id,
      ]);
      return;
    }

    const baseline = structuredClone(
      extractDomainConfig(domain, mergedResponse.config),
    );
    this.baselineConfig.set(baseline);
    this.editingVersion.set(editingResponse);

    if (domain === 'storage') {
      this.patchFormFromConfig(
        domain,
        mergeStorageServiceRatesForForm(
          editingResponse.config as PricingDomainConfigMap['storage'],
          baseline as PricingDomainConfigMap['storage'],
        ),
      );
    } else {
      this.patchFormFromConfig(domain, editingResponse.config);
    }
    this.form.patchValue({
      effectiveFrom: editingResponse.effective_from,
      comment: editingResponse.comment ?? '',
    });
  }

  private isFutureVersion(
    version: AdminDomainPricingVersionDetailResponse<PricingDomain>,
  ): boolean {
    return version.effective_from > todayMinskIsoDate();
  }

  private patchFormFromConfig(
    domain: PricingDomain,
    config: PricingDomainConfigMap[PricingDomain],
  ): void {
    if (domain === 'storage') {
      const storage = config as PricingDomainConfigMap['storage'];
      this.form.patchValue({
        storage: {
          baseRateByndPerM3Day: storage.baseRateByndPerM3Day,
          minVolumeM3: storage.minVolumeM3,
          maxPromoDiscountPercent: storage.maxPromoDiscountPercent,
          serviceRates: { ...storage.serviceRates },
        },
      });

      this.volumeDiscountTiers.clear();
      for (const tier of storage.volumeDiscountTiers) {
        this.volumeDiscountTiers.push(
          this.#fb.group({
            minVolumeM3: [tier.minVolumeM3 ?? 0, Validators.required],
            discountPercent: [tier.discountPercent, Validators.required],
          }),
        );
      }
      return;
    }

    if (domain === 'moving') {
      const moving = config as PricingDomainConfigMap['moving'];
      this.form.patchValue({
        movingRates: Object.fromEntries(
          MOVING_RATE_FIELDS.map((field) => [field, moving.rates[field]]),
        ),
        disassemblyRates: { ...moving.rates.disassemblyByndPerPiece },
        assemblyRates: { ...moving.rates.assemblyByndPerPiece },
      });
      return;
    }

    const promocodes = config as PricingDomainConfigMap['promocodes'];
    this.promocodes.clear();
    for (const promo of promocodes.promocodes) {
      this.promocodes.push(
        this.#fb.group({
          promocode: [promo.promocode, Validators.required],
          discount: [promo.discount, Validators.required],
          commission: [promo.commission ?? null],
        }),
      );
    }
  }

  private buildConfigFromForm(
    value: ReturnType<typeof this.form.getRawValue>,
  ): PricingDomainConfigMap[PricingDomain] | null {
    const baseline = this.baselineConfig();
    if (!baseline) {
      return null;
    }

    const domain = this.domain();

    if (domain === 'storage') {
      return normalizeDomainConfigForApi(
        domain,
        {
          baseRateByndPerM3Day: value.storage?.baseRateByndPerM3Day ?? 0,
          minVolumeM3: value.storage?.minVolumeM3 ?? 0,
          serviceRates: value.storage?.serviceRates ?? {
            pickupByndPerM3: 0,
            returnByndPerM3: 0,
            packagingByndPerM3: 0,
            pickupMinVolumeM3: 0,
            returnMinVolumeM3: 0,
            packagingMinVolumeM3: 0,
          },
          maxPromoDiscountPercent: value.storage?.maxPromoDiscountPercent ?? 0,
          volumeDiscountTiers: (value.volumeDiscountTiers ?? []).map((tier) => ({
            minVolumeM3: tier?.['minVolumeM3'] ?? 0,
            discountPercent: tier?.['discountPercent'] ?? 0,
          })),
        } as PricingDomainConfigMap['storage'],
      );
    }

    if (domain === 'moving') {
      const base = baseline as PricingDomainConfigMap['moving'];
      const movingRates = value.movingRates ?? {};
      const disassemblyRates = value.disassemblyRates ?? {};
      const assemblyRates = value.assemblyRates ?? {};

      return normalizeDomainConfigForApi(domain, {
        rates: {
          ...base.rates,
          ...movingRates,
          disassemblyByndPerPiece: {
            ...base.rates.disassemblyByndPerPiece,
            ...disassemblyRates,
          },
          assemblyByndPerPiece: {
            ...base.rates.assemblyByndPerPiece,
            ...assemblyRates,
          },
        },
      } satisfies PricingDomainConfigMap['moving']);
    }

    return normalizeDomainConfigForApi(domain, {
      promocodes: (value.promocodes ?? [])
        .filter((entry) => entry?.['promocode']?.trim())
        .map((entry) => ({
          promocode: String(entry?.['promocode'] ?? '').trim(),
          discount: entry?.['discount'] ?? 0,
          ...(entry?.['commission'] != null && entry['commission'] !== ''
            ? { commission: entry['commission'] }
            : {}),
        })),
    } satisfies PricingDomainConfigMap['promocodes']);
  }
}
